import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowUp, ClipboardList, Clock, Eye, EyeOff, FileText, Languages, List, Loader2, FileWarning, Pencil, Printer, Sparkles } from "lucide-react";
import hljs from "highlight.js/lib/common";
import "highlight.js/styles/github-dark.css";
import api from "@/lib/api";
import { enhanceKbContent, collectCodeBlocks, copyText } from "@/lib/kbContentRuntime";
import { Button } from "@/components/ui/button";
import { KBLanguageSwitcher } from "@/components/KBLanguageSwitcher";
import { useKbLang, KB_ORIGINAL, langMeta } from "@/lib/kbLang";
import { KbEditDialog } from "@/components/KbEditDialog";
import { KbFeedback } from "@/components/KbFeedback";
import { KbPdfDialog } from "@/components/KbPdfDialog";
import { useKbPrintPrep } from "@/lib/kbPrint";
import { KbNewPill } from "@/components/KbNewPill";
import { KbSnippetPill } from "@/components/KbSnippetPill";
import { KbStyleInjector } from "@/lib/kbSettings";
import { sanitizeKbHtml, useKbEmbedHosts } from "@/lib/kbSanitize";
import { audienceSummary } from "@/lib/kbAudiences";
import { usePageBreadcrumb } from "@/context/BreadcrumbContext";

export default function KBArticle() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [a, setA] = useState(null);
  const [editing, setEditing] = useState(false);
  const [tableCount, setTableCount] = useState(0);
  const [toc, setToc] = useState([]);
  const [showTop, setShowTop] = useState(false);
  const [codeCount, setCodeCount] = useState(0);
  const [copiedAll, setCopiedAll] = useState(false);
  const [lang, setLang] = useKbLang();
  const [tr, setTr] = useState(null);
  const [translating, setTranslating] = useState(false);
  const [transError, setTransError] = useState(false);

  /** One paste with every config block on the page, each under the heading it
   *  sat below, so a tech can work down an SBC setup without hopping back. */
  const copyAllCode = async () => {
    const blocks = collectCodeBlocks(document.getElementById("kb-article-body"));
    if (!blocks.length) return;
    const text = blocks.map((b) => (b.heading ? `# ${b.heading}\n${b.code}` : b.code))
      .join("\n\n");
    await copyText(`# ${a?.name || "Knowledge Base article"}\n\n${text}\n`);
    setCopiedAll(true);
    setTimeout(() => setCopiedAll(false), 1800);
  };

  useEffect(() => {
    setA(null);
    api.get(`/kb/articles/${slug}`).then((r) => setA(r.data))
      .catch((e) => setA({ error: true, forbidden: e.response?.status === 403,
                           reason: e.response?.data?.detail || "" }));
    window.scrollTo({ top: 0 });
  }, [slug]);

  // AI-translate the article when the reader has picked a flag (display only —
  // the stored article stays English). Runs as a background job the server
  // caches; we poll until it's ready to beat the request timeout on long pages.
  useEffect(() => {
    if (!a || a.error || lang === KB_ORIGINAL) {
      setTr(null); setTranslating(false); setTransError(false);
      return undefined;
    }
    let cancelled = false;
    let timer;
    setTranslating(true); setTransError(false); setTr(null);
    const poll = () => {
      api.get(`/kb/articles/${slug}/translate`, { params: { lang } })
        .then((r) => {
          if (cancelled) return;
          const d = r.data || {};
          if (d.status === "ready") { setTr(d); setTranslating(false); }
          else if (d.status === "error") { setTransError(true); setTranslating(false); }
          else { timer = setTimeout(poll, 2500); }
        })
        .catch(() => { if (!cancelled) { setTransError(true); setTranslating(false); } });
    };
    poll();
    return () => { cancelled = true; if (timer) clearTimeout(timer); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug, lang, a?.article_id, a?.error]);

  // Imported tables can be wider than the column, and Helpjuice often built
  // header rows out of <td>. Wrap each table so it scrolls instead of
  // spilling, and intercept internal /kb/ links so they stay in the SPA.
  useEffect(() => {
    const root = document.getElementById("kb-article-body");
    if (!root) return;
    const tables = root.querySelectorAll("table");
    setTableCount(tables.length);
    // In-page nav: give every heading a stable anchor and build the contents list.
    const heads = [...root.querySelectorAll("h2, h3")].filter((h) => h.textContent.trim());
    heads.forEach((h, i) => { if (!h.id) h.id = `kb-h-${i}`; });
    setToc(heads.map((h) => ({ id: h.id, text: h.textContent.trim(), level: h.tagName === "H3" ? 3 : 2 })));
    root.querySelectorAll("pre code").forEach((el) => {
      if (!el.dataset.highlighted) hljs.highlightElement(el);
    });
    setCodeCount(root.querySelectorAll("pre").length);
    tables.forEach((t, i) => {
      const wrap = t.parentElement?.classList.contains("kb-table-wrap")
        ? t.parentElement
        : (() => {
            const w = document.createElement("div");
            w.className = "kb-table-wrap";
            t.parentNode.insertBefore(w, t);
            w.appendChild(t);
            return w;
          })();
      // Per-table border override set by an editor, plus source tables that
      // already asked for border="0" (flagged at import time).
      const borderless = a?.table_styles?.[i]?.borderless || t.classList.contains("kb-table-noborder");
      wrap.classList.toggle("borderless", !!borderless);
    });
    // Centre images and let them fill the column up to their natural width so
    // small screenshots aren't stretched into a pixelated mess.
    root.querySelectorAll("img").forEach((img) => {
      // Respect an author-chosen width (image panel) instead of auto-fitting.
      if (img.getAttribute("width") || img.style.width) {
        img.classList.add("kb-img-fitted");
        return;
      }
      const fit = () => {
        if (img.naturalWidth >= 480) img.style.width = `min(100%, ${img.naturalWidth}px)`;
        img.classList.add("kb-img-fitted");
      };
      if (img.complete && img.naturalWidth) fit();
      else img.addEventListener("load", fit, { once: true });
    });
    const onClick = (e) => {
      const link = e.target.closest("a[href^='/kb/']");
      if (!link) return;
      e.preventDefault();
      navigate(link.getAttribute("href"));
    };
    root.addEventListener("click", onClick);
    return () => root.removeEventListener("click", onClick);
  }, [a, tr, navigate]);

  // Runs after the body (and the contents sidebar, which can remount it) has
  // settled, so tab bars, TOC blocks, equations and internal links stick.
  useEffect(() => {
    const root = document.getElementById("kb-article-body");
    if (!root) return;
    // New article in the same container — let the glossary pass run again.
    if (root.dataset.kbGlossarySlug !== slug) {
      delete root.dataset.kbGlossary;
      root.dataset.kbGlossarySlug = slug;
    }
    enhanceKbContent(root, { glossary: a?.glossary_off !== true });
  }, [a, tr, toc.length, slug]);

  useEffect(() => {
    const onScroll = () => setShowTop(window.scrollY > 600);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Iter 250 — defence in depth: article HTML is already sanitised with nh3 on
  // import/save, sanitise again on render so a future unsanitised write path
  // cannot turn into stored XSS.
  // Iter 253 — the render-time sanitise used DOMPurify's defaults, which strip
  // every <iframe>: PDF and video embeds became empty boxes for readers while
  // still showing in the editor. Iframes now survive when their src is on the
  // KB embed allow-list (or is one of our own /api/kb/assets/ files).
  const embedHosts = useKbEmbedHosts();
  const bodyHtml = useMemo(
    () => ({ __html: sanitizeKbHtml((tr?.body_html ?? a?.body_html) || "<p>This article has no content yet.</p>", embedHosts) }),
    [tr?.body_html, a?.body_html, embedHosts]);
  const sectionHtml = useMemo(
    () => ((tr?.sections ?? a?.sections) || []).map((s) => ({ __html: sanitizeKbHtml(s.body_html || "", embedHosts) })),
    [tr?.sections, a?.sections, embedHosts]);
  const displayName = tr?.title || a?.name;
  const activeLang = langMeta(lang);
  useKbPrintPrep(displayName);

  // Iter 239 — feed the single global breadcrumb (Layout) with the real
  // category chain instead of painting a second crumb row on the page.
  usePageBreadcrumb(useMemo(() => {
    if (!a || a.error) return null;
    return [
      { label: "Knowledge Base", to: "/kb" },
      ...(a.breadcrumbs || []).filter((t) => t.name.trim() !== "Knowledge Base")
        .map((t) => ({ label: t.name.trim(), to: `/kb/c/${t.slug}` })),
      { label: displayName || "Article" },
    ];
  }, [a, displayName]));

  if (!a) return <div className="flex h-72 items-center justify-center"><Loader2 className="h-5 w-5 animate-spin text-slate-300 dark:text-slate-600" /></div>;
  if (a.error) return (
    <div className="kb-surface" data-testid="kb-article-missing">
      <div className="mx-auto mt-16 max-w-md rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 px-6 py-10 text-center">
        <FileWarning className="mx-auto h-8 w-8 text-slate-300 dark:text-slate-600" />
        {a.forbidden ? (
          <>
            <p className="mt-3 text-sm font-medium text-slate-600 dark:text-slate-300" data-testid="kb-article-forbidden-reason">
              {a.reason || "This article is internal to staff only."}
            </p>
            <p className="mt-1 text-xs text-slate-400 dark:text-slate-500">Ask the article&apos;s owner if you need access.</p>
          </>
        ) : (
          <p className="mt-3 text-sm text-slate-500 dark:text-slate-400">Article not found.</p>
        )}
        <button onClick={() => navigate("/kb")} className="mt-4 text-sm font-medium text-primary hover:underline" data-testid="kb-article-back">
          ← Back to the Knowledge Base
        </button>
      </div>
    </div>
  );

  return (
    <article data-testid="kb-article-page" id="kb-print-root" className="kb-surface">
      <KbStyleInjector />

      <header className="border-b border-slate-200 dark:border-slate-800 pb-5">
        {/* Iter 120 — print-only masthead so a printed page still says where
            the article came from (screen users get the breadcrumb nav above). */}
        <div className="kb-print-head hidden text-[10pt] text-slate-500 print:flex" data-testid="kb-print-masthead">
          <span>
            Conduit Knowledge Base
            {(a.breadcrumbs || []).filter((t) => t.name.trim() !== "Knowledge Base")
              .map((t) => ` › ${t.name.trim()}`).join("")}
          </span>
          <span className="ml-auto">
            {new Date().toLocaleDateString([], { day: "numeric", month: "long", year: "numeric" })}
            {" · "}
            <a href={typeof window !== "undefined" ? window.location.href.split("#")[0] : ""}
              data-testid="kb-print-view-online">View online</a>
          </span>
        </div>
        <div className="flex flex-wrap items-center gap-2 print:hidden">
          {a.is_draft && <span className="rounded bg-amber-100 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-amber-700" data-testid="kb-article-draft-badge">Draft</span>}
          {a.has_table && <span className="rounded bg-slate-100 dark:bg-slate-800 px-2 py-0.5 text-[10px] font-medium text-slate-500 dark:text-slate-400">Tables</span>}
          <span className="rounded bg-slate-100 dark:bg-slate-800 px-2 py-0.5 text-[10px] font-medium text-slate-500 dark:text-slate-400" data-testid="kb-article-scope">
            {a.scope === "external" ? "All scopes" : "Staff only"}
          </span>
          {a.image_count > 0 && <span className="rounded bg-slate-100 dark:bg-slate-800 px-2 py-0.5 text-[10px] font-medium text-slate-500 dark:text-slate-400">{a.image_count} images</span>}
        </div>
        <div className="mt-2 flex items-start justify-between gap-4">
          <h1 className="flex flex-wrap items-center gap-2 font-heading text-3xl font-bold leading-tight text-slate-900 dark:text-slate-100" data-testid="kb-article-title">
            {displayName}
            {a.is_new && <KbNewPill />}
            {a.is_snippet && <KbSnippetPill />}
          </h1>
          <div className="flex shrink-0 items-center gap-2 print:hidden">
            <KBLanguageSwitcher busy={translating} />
            <Button variant="outline" size="sm" data-testid="kb-print-button"
              onClick={() => window.print()} title="Print this article">
              <Printer className="h-4 w-4" /> Print
            </Button>
            <KbPdfDialog />
            {codeCount > 1 && (
              <Button variant="outline" size="sm" data-testid="kb-copy-all-code"
                onClick={copyAllCode}
                title={`Copy all ${codeCount} config blocks in order`}>
                <ClipboardList className="h-4 w-4" /> {copiedAll ? "Copied" : "Copy all config"}
              </Button>
            )}
            <Button variant="outline" size="sm" data-testid="kb-ask-ai-button"
              onClick={() => navigate(`/ask-claude?ask=${encodeURIComponent(`About the KB article "${a.name}": `)}`)}
              className="border-primary/40 text-primary hover:bg-primary/5">
              <Sparkles className="h-4 w-4" /> Ask AI about this
            </Button>
            {a.can_edit && (
              <>
                <Button variant="outline" size="sm" data-testid="kb-edit-article-button"
                  onClick={() => navigate(`/kb/a/${slug}/edit`)}>
                  <Pencil className="h-4 w-4" /> Edit
                </Button>
                <Button variant="ghost" size="sm" data-testid="kb-quick-edit-button"
                  title="Quick edit in a dialog" onClick={() => setEditing(true)}>
                  Quick edit
                </Button>
              </>
            )}
          </div>
        </div>
        <div className="mt-3 flex flex-wrap items-center gap-4 text-xs text-slate-400 dark:text-slate-500">
          <span className="flex items-center gap-1 print:hidden"><Eye className="h-3.5 w-3.5" /> {(a.hj_views || 0).toLocaleString()} views</span>
          <span className="flex items-center gap-1"><Clock className="h-3.5 w-3.5" /> updated {(a.source_updated_at || "").slice(0, 10)}</span>
          <span>{a.word_count} words</span>
          <span data-testid="kb-article-readtime">{Math.max(1, Math.round((a.word_count || 0) / 200))} min read</span>
          {a.edited_by && <span className="print:hidden">last edited by {a.edited_by}</span>}
        </div>
      </header>

      {/* Editors only — a reader can't re-upload a screenshot, so the notice
          would just tell them the page is broken. The inline placeholder chips
          in the body still show where an image is genuinely missing. */}
      {a.can_edit && a.pending && (
        <div className="mt-5 flex items-start gap-3 rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 print:hidden"
          data-testid="kb-article-pending-notice">
          <FileWarning className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
          <div className="text-xs text-amber-900">
            <span className="font-semibold">Editors only:</span>{" "}
            a newer version{a.pending.by_name ? ` by ${a.pending.by_name}` : ""} is {a.review_state === "in_review" ? "under review" : "waiting to be submitted for review"}
            {a.reviewer_name ? ` (reviewer: ${a.reviewer_name})` : ""}. Readers see this published version until it&apos;s approved.{" "}
            <Link to={`/kb/a/${a.slug}/edit`} className="font-medium underline" data-testid="kb-article-pending-open">Open the pending version</Link>
          </div>
        </div>
      )}

      {a.can_edit && a.missing_asset_count > 0 && (
        <div className="mt-5 flex items-start gap-3 rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 print:hidden"
          data-testid="kb-article-missing-assets">
          <FileWarning className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
          <div className="text-xs text-amber-900">
            <span className="font-semibold">Editors only:</span>{" "}
            {a.missing_asset_count} attachment{a.missing_asset_count === 1 ? "" : "s"} in this article couldn&apos;t be
            imported from Helpjuice (private S3 storage returns 403) and still need re-uploading — they&apos;re marked
            inline below. This notice disappears on its own once you replace them and save.
          </div>
        </div>
      )}

      <div className="kb-article-layout mt-6 flex flex-col-reverse gap-8 lg:flex-row lg:items-start">
      <div id="kb-article-body" className="kb-article-main min-w-0 flex-1 lg:order-1" data-testid="kb-article-body">
        {activeLang && !transError && (
          <div className="mb-4 flex items-center gap-2 rounded-lg border border-primary/20 bg-primary/5 px-3 py-2 text-xs text-slate-600 dark:text-slate-300 print:hidden"
            data-testid="kb-translated-note">
            <Languages className="h-3.5 w-3.5 text-primary" />
            {translating
              ? <>Translating into {activeLang.label}…</>
              : <>Machine-translated into <span className="font-semibold">{activeLang.label}</span>. The original is in English.</>}
            <button onClick={() => setLang(KB_ORIGINAL)} className="ml-auto font-medium text-primary hover:underline"
              data-testid="kb-translated-show-original">Show original</button>
          </div>
        )}
        {transError && (
          <div className="mb-4 flex items-center gap-2 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900 print:hidden"
            data-testid="kb-translate-error">
            <FileWarning className="h-3.5 w-3.5" />
            Couldn&apos;t translate this article right now — showing the English original.
          </div>
        )}
        <div className="kb-prose" dangerouslySetInnerHTML={bodyHtml} />
        {(tr?.sections ?? a.sections ?? []).map((s, i) => (
          <div key={s.id || i} data-testid={`kb-article-section-${i}`}
            className={`mt-8 ${s.audiences !== undefined && !s.visible ? "rounded-lg border border-dashed border-slate-200 dark:border-slate-800 bg-slate-50/50 p-4" : ""}`}>
            {s.audiences !== undefined && (
              <div className="mb-2 flex items-center gap-1.5 text-[11px] font-medium uppercase tracking-wide text-slate-400 dark:text-slate-500"
                data-testid={`kb-article-section-label-${i}`}>
                {s.visible ? <Eye className="h-3.5 w-3.5" /> : <EyeOff className="h-3.5 w-3.5" />}
                Visible to: {audienceSummary(s.audiences)}
              </div>
            )}
            <div className="kb-prose" dangerouslySetInnerHTML={sectionHtml[i]} />
          </div>
        ))}
      </div>
      {toc.length >= 2 && (
        <aside className="kb-article-toc w-full shrink-0 lg:sticky lg:order-2 lg:w-56" data-testid="kb-article-toc"
          style={{ top: "calc(var(--app-header-h, 0px) + 1.5rem)" }}>
          <div className="kb-article-toc-box rounded-xl border border-slate-200 dark:border-slate-800 bg-white/70 p-4">
            <div className="kb-article-toc-head mb-2 flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-slate-400 dark:text-slate-500">
              <List className="h-3.5 w-3.5 print:hidden" />
              <span className="print:hidden">On this page</span>
              <span className="hidden print:inline">Contents</span>
            </div>
            <nav className="space-y-1">
              {toc.map((h) => (
                <a key={h.id} href={`#${h.id}`} data-testid={`kb-toc-${h.id}`}
                  onClick={(e) => {
                    e.preventDefault();
                    document.getElementById(h.id)?.scrollIntoView({ behavior: "smooth", block: "start" });
                    window.history.replaceState(null, "", `#${h.id}`);
                  }}
                  className={`kb-article-toc-link block truncate text-xs no-underline transition-colors hover:text-primary hover:underline ${
                    h.level === 3 ? "kb-article-toc-sub pl-3 text-slate-400 dark:text-slate-500" : "text-slate-600 dark:text-slate-300"}`}>
                  {h.text}
                </a>
              ))}
            </nav>
          </div>
        </aside>
      )}
      </div>

      <div className="hidden border-t border-slate-300 dark:border-slate-700 pt-2 text-[9pt] text-slate-500 dark:text-slate-400 print:block"
        data-testid="kb-print-footer">
        {typeof window !== "undefined" ? window.location.href : ""} · printed {new Date().toLocaleDateString()}
      </div>

      <div className="print:hidden">
        {!!(a.keywords || []).length && (
          <div className="mt-8 flex flex-wrap gap-2 print:hidden" data-testid="kb-article-keywords">
            {a.keywords.map((k) => (
              <Link key={k} to={`/kb?q=${encodeURIComponent(k)}`}
                data-testid={`kb-article-keyword-${k.replace(/\s+/g, "-")}`}
                title={`Search the knowledge base for “${k}”`}
                className="rounded bg-slate-100 px-2 py-1 text-[11px] font-medium uppercase tracking-wide text-slate-600 no-underline transition-colors hover:bg-primary/10 hover:text-primary dark:bg-slate-800 dark:text-slate-300">
                {k}
              </Link>
            ))}
          </div>
        )}
        <KbFeedback slug={slug} initial={a.my_feedback} counts={a.feedback_counts} />
      </div>

      {(a.related || []).length > 0 && (
        <section className="mt-12 border-t border-slate-200 dark:border-slate-800 pt-6 print:hidden" data-testid="kb-related">
          <h2 className="mb-3 font-heading text-sm font-semibold uppercase tracking-wide text-slate-400 dark:text-slate-500">Related articles</h2>
          <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
            {a.related.map((r) => (
              <button key={r.article_id} data-testid={`kb-related-${r.slug}`} onClick={() => navigate(`/kb/a/${r.slug}`)}
                className="flex items-center gap-2 rounded-lg border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 px-4 py-3 text-left transition-colors hover:border-primary/40 hover:bg-slate-50 dark:hover:bg-slate-800">
                <FileText className="h-4 w-4 shrink-0 text-slate-300 dark:text-slate-600" />
                <span className="min-w-0 truncate text-sm text-slate-700 dark:text-slate-200">{r.name}</span>
              </button>
            ))}
          </div>
        </section>
      )}
      {showTop && (
        <button type="button" data-testid="kb-back-to-top"
          onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
          className="fixed bottom-24 right-6 z-30 flex h-10 w-10 items-center justify-center rounded-full border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 text-slate-500 dark:text-slate-400 shadow-lg transition-colors hover:border-primary/40 hover:text-primary print:hidden"
          title="Back to top">
          <ArrowUp className="h-4 w-4" />
        </button>
      )}
      {editing && (
        <KbEditDialog open={editing} onOpenChange={setEditing} kind="article" record={a}
          tableCount={tableCount} onSaved={(d) => setA({ ...a, ...d })}
          onDeleted={() => navigate(a.primary_category_id && a.breadcrumbs?.length
            ? `/kb/c/${a.breadcrumbs[a.breadcrumbs.length - 1].slug}` : "/kb")} />
      )}
    </article>
  );
}
